import i18next from "i18next";
import { DEFAULT_LOCALE, intlLocaleFor } from "../i18n/locale";

/** Dates, in the two shapes this dashboard shows them. */

/**
 * A date, without a time.
 *
 * Everything here happened on a day rather than at a moment — a signup, a
 * conversion, a payout — and a timestamp to the second would invite somebody to
 * reconcile against a clock that is not theirs. The one place a time would
 * matter is a hold window running out, and that is expressed as "in 12 days"
 * instead, which is the question actually being asked.
 */
/**
 * The tag dates are formatted with. English keeps the browser's own region, as
 * it always has; any other language formats the way that language does -- the
 * customer dashboard's rule, for the same reader.
 */
function formatLocale(): string | undefined {
  const language = i18next.language;
  return !language || language === DEFAULT_LOCALE ? undefined : intlLocaleFor(language);
}

const commonT = () => i18next.getFixedT(null, "common");

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(formatLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * How many days away a date is, counted in calendar days rather than in hours.
 *
 * The distinction is the whole reason this is a function. Ceiling the elapsed
 * hours is the obvious implementation and gets the two labels a reader actually
 * notices backwards: a hold clearing at 13:00 today, read at noon, is one hour
 * away and reports as "tomorrow", and one clearing at 23:00 today read at 01:00
 * does the same. Both are wrong about the day the thing happens on, which is
 * the only thing the label claims.
 *
 * So both sides are truncated to midnight *local* — local because the reader is
 * the one who has to recognise "today", and their day is the one on their
 * clock rather than the one on the server's.
 *
 * Rounded rather than floored, and that is not defensive: across a daylight
 * saving boundary two local midnights are 23 or 25 hours apart, so the division
 * yields 0.958 or 1.042 and a floor would report the day before a clock change
 * as "today".
 *
 * Never negative. A commission whose window passed before the cron ripened it
 * would otherwise render as "in -2 days".
 */
export function daysUntil(iso: string, now = new Date()): number {
  const midnight = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const days = (midnight(new Date(iso)) - midnight(now)) / 86_400_000;
  return Math.max(0, Math.round(days));
}

/** "in 12 days", "tomorrow", "today" — a hold window, said the way it is asked about. */
export function formatCountdown(iso: string, now = new Date()): string {
  const days = daysUntil(iso, now);
  const t = commonT();
  if (days === 0) return t("time.today");
  if (days === 1) return t("time.tomorrow");
  return t("time.inDays", { count: days });
}

/**
 * Where a transfer can be checked, by anybody.
 *
 * A block explorer rather than anything of ours, and that is the point: an
 * affiliate asking "was I actually paid" gets an answer from a third party we
 * do not control, which is a stronger thing than a status on our own dashboard.
 * PayPal's batch id could only ever be looked up by us.
 *
 * Keyed on the chain the API sends rather than hard-coded, so a payout made on
 * some future network does not silently link to the wrong explorer. Unknown
 * chains get null and the interface shows the hash without a link — see
 * `TxLink`.
 */
const EXPLORERS: Record<string, string> = {
  base: "https://basescan.org/tx/",
};

export function explorerUrl(chain: string, txHash: string): string | null {
  const base = EXPLORERS[chain];
  return base ? `${base}${txHash}` : null;
}

/** A hash, shortened for a table cell. The ends are what somebody compares. */
export function shortHash(value: string): string {
  return value.length <= 18 ? value : `${value.slice(0, 10)}…${value.slice(-6)}`;
}

/**
 * How long something has been waiting, in the coarsest honest unit.
 *
 * For the payout queue, where the question is never "when exactly" but "is
 * anybody being left". A date answers the first and makes somebody do the
 * subtraction for the second — and a reviewer scanning a queue will not do
 * arithmetic on ten rows, so they simply do not notice the one from last week.
 *
 * Coarse on purpose: "6 days" and not "5 days, 14 hours". The decision this
 * feeds is whether to look at a row now, and precision past the unit only makes
 * the column harder to scan.
 */
export function formatWaitingFor(iso: string, now = new Date()): string {
  const minutes = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
  const t = commonT();
  if (minutes < 60) return minutes <= 1 ? t("time.justNow") : t("time.minutes", { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t("time.hours", { count: hours });
  const days = Math.round(hours / 24);
  return t("time.days", { count: days });
}

/**
 * Days since an instant, for deciding whether a wait is worth marking.
 *
 * Separate from the label above because a threshold and a rendering are
 * different jobs: parsing "6 days" back out of a string to compare it would be
 * the kind of cleverness that breaks the first time the wording changes.
 */
export function daysSince(iso: string, now = new Date()): number {
  return (now.getTime() - new Date(iso).getTime()) / 86_400_000;
}

/**
 * A wallet address with its middle removed.
 *
 * Both ends kept, because that is what somebody actually compares an address
 * against — nobody reads the middle twenty characters, and a table of full
 * addresses wraps to two lines a row and stops being scannable.
 *
 * Only ever shown beside a control that copies the whole thing. Shortening a
 * value somebody has to retype would be the mistake this avoids.
 */
export function shortAddress(value: string): string {
  return value.length <= 16 ? value : `${value.slice(0, 8)}…${value.slice(-6)}`;
}
